import { Easing, interpolate } from 'remotion'

/** Opacity and volume ramps shared by every layer (Editor Starter port). */

export const fadeEasingFunction = (t: number) => Easing.ease(t)

export function calculateFadeInOpacity({
  currentFrame,
  fadeInDurationInSeconds,
  framesPerSecond,
}: {
  currentFrame: number
  fadeInDurationInSeconds: number
  framesPerSecond: number
}): number {
  if (fadeInDurationInSeconds === 0) return 1
  const progress = Math.min(
    1,
    currentFrame / (fadeInDurationInSeconds * framesPerSecond),
  )
  return fadeEasingFunction(progress)
}

export function calculateFadeOutOpacity({
  currentFrame,
  fadeOutDurationInSeconds,
  framesPerSecond,
  totalDurationInFrames,
}: {
  currentFrame: number
  fadeOutDurationInSeconds: number
  framesPerSecond: number
  totalDurationInFrames: number
}): number {
  if (fadeOutDurationInSeconds === 0) return 1
  const fadeOutStartFrame =
    totalDurationInFrames - fadeOutDurationInSeconds * framesPerSecond
  const progress = Math.max(
    0,
    1 -
      (currentFrame - fadeOutStartFrame) /
        (fadeOutDurationInSeconds * framesPerSecond),
  )
  return fadeEasingFunction(progress)
}

export function useFadeOpacity({
  frame,
  fps,
  durationInFrames,
  fadeInDurationInSeconds,
  fadeOutDurationInSeconds,
  opacity,
}: {
  frame: number
  fps: number
  durationInFrames: number
  fadeInDurationInSeconds: number
  fadeOutDurationInSeconds: number
  opacity: number
}): number {
  return (
    calculateFadeInOpacity({
      currentFrame: frame,
      fadeInDurationInSeconds,
      framesPerSecond: fps,
    }) *
    calculateFadeOutOpacity({
      currentFrame: frame,
      fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: durationInFrames,
    }) *
    opacity
  )
}

/**
 * The loudest the volume controls go: the whole dB below the schema's
 * `MAX_VOLUME` gain (4×, about +12 dB), so the preview never plays louder
 * than a save keeps and Lambda renders. `volume.test.ts` keeps them aligned.
 */
export const MAX_VOLUME_DB = 12
export const MIN_VOLUME_DB = -60

export function decibelToGain(decibel: number): number {
  if (decibel === MIN_VOLUME_DB) return 0
  return Math.pow(10, decibel / 20)
}

export function gainToDecibel(gain: number): number {
  if (gain <= 0) return MIN_VOLUME_DB
  return 20 * Math.log10(gain)
}

/**
 * The volume controls speak percent, not dB (#541): 100% is the clip as
 * recorded, 0% silent. State and saves keep the dB adjustment and the gain.
 */
export const MAX_VOLUME_PERCENT = 200

export function decibelToPercent(decibel: number): number {
  return Math.round(decibelToGain(decibel) * 100)
}

export function percentToDecibel(percent: number): number {
  return percent <= 0 ? MIN_VOLUME_DB : gainToDecibel(percent / 100)
}

function getFadeInMultiplier(frame: number, fadeInFrames: number): number {
  if (fadeInFrames <= 0) return 1
  return interpolate(frame, [0, fadeInFrames], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: fadeEasingFunction,
  })
}

function getFadeOutMultiplier(
  frame: number,
  fadeOutStartFrame: number,
  durationInFrames: number,
): number {
  if (fadeOutStartFrame >= durationInFrames) return 1
  return interpolate(frame, [fadeOutStartFrame, durationInFrames], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(fadeEasingFunction),
  })
}

export function getAudioFadeMultiplier({
  frame,
  fadeInFrames,
  fadeOutFrames,
  durationInFrames,
}: {
  frame: number
  fadeInFrames: number
  fadeOutFrames: number
  durationInFrames: number
}): number {
  if (durationInFrames <= 0) return 0
  if (fadeInFrames <= 0 && fadeOutFrames <= 0) return 1
  const clampedFadeIn = Math.min(fadeInFrames, durationInFrames)
  const clampedFadeOut = Math.min(fadeOutFrames, durationInFrames)
  const fadeOutStartFrame = durationInFrames - clampedFadeOut
  return (
    getFadeInMultiplier(frame, clampedFadeIn) *
    getFadeOutMultiplier(frame, fadeOutStartFrame, durationInFrames)
  )
}

export const volumeFn =
  ({
    fps,
    audioFadeInDurationInSeconds,
    audioFadeOutDurationInSeconds,
    durationInFrames,
    decibelAdjustment,
  }: {
    fps: number
    audioFadeInDurationInSeconds: number
    audioFadeOutDurationInSeconds: number
    durationInFrames: number
    decibelAdjustment: number
  }) =>
  (frame: number) =>
    decibelToGain(decibelAdjustment) *
    getAudioFadeMultiplier({
      frame,
      fadeInFrames: audioFadeInDurationInSeconds * fps,
      fadeOutFrames: audioFadeOutDurationInSeconds * fps,
      durationInFrames,
    })
